/**
 * NavController — topbar dropdown menus + mobile sidebar drawer.
 *
 * Attached to <body> on every page that uses layouts/app.html. Reads
 * `data-menu-trigger`/`#menu-<name>` for dropdown menus and
 * `data-sidebar-toggle`/`data-sidebar-backdrop` for the mobile drawer.
 *
 * Behaviors:
 *   - click on a trigger toggles the matching menu (and closes the others)
 *   - click outside any menu closes them all
 *   - hamburger toggles the sidebar drawer + backdrop on mobile
 *   - click on backdrop closes the drawer
 *   - Escape closes both menus and drawer
 */

import { Controller } from "/js/vendor/stimulus.js";

export default class NavController extends Controller {
  connect() {
    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.element.addEventListener("click", this.onClick);
    document.addEventListener("keydown", this.onKeydown);
  }

  disconnect() {
    this.element.removeEventListener("click", this.onClick);
    document.removeEventListener("keydown", this.onKeydown);
  }

  onClick(event) {
    const trigger = event.target.closest("[data-menu-trigger]");
    if (trigger) {
      const name = trigger.getAttribute("data-menu-trigger");
      const menu = document.getElementById(`menu-${name}`);
      if (menu) {
        const willOpen = !menu.classList.contains("is-open");
        this.closeAllMenus();
        if (willOpen) {
          menu.classList.add("is-open");
          menu.setAttribute("aria-hidden", "false");
          trigger.setAttribute("aria-expanded", "true");
        }
      }
      event.stopPropagation();
      return;
    }

    const hamburger = event.target.closest("[data-sidebar-toggle]");
    if (hamburger) {
      const sidebar = document.querySelector(".nm-app__sidebar");
      const backdrop = document.querySelector("[data-sidebar-backdrop]");
      if (sidebar) {
        const open = sidebar.classList.toggle("is-open");
        if (backdrop) {
          backdrop.classList.toggle("is-open", open);
          backdrop.hidden = !open;
        }
        document.body.style.overflow = open ? "hidden" : "";
      }
      event.stopPropagation();
      return;
    }

    if (event.target.closest("[data-sidebar-backdrop]")) {
      this.closeDrawer();
      document.body.style.overflow = "";
      return;
    }

    if (!event.target.closest(".nm-menu")) {
      this.closeAllMenus();
    }
  }

  onKeydown(event) {
    if (event.key === "Escape") {
      this.closeAllMenus();
      this.closeDrawer();
      document.body.style.overflow = "";
    }
  }

  closeAllMenus() {
    document.querySelectorAll(".nm-menu.is-open").forEach((menu) => {
      menu.classList.remove("is-open");
      menu.setAttribute("aria-hidden", "true");
      const name = menu.id.replace("menu-", "");
      const trigger = document.querySelector(`[data-menu-trigger="${name}"]`);
      if (trigger) trigger.setAttribute("aria-expanded", "false");
    });
  }

  closeDrawer() {
    const sidebar = document.querySelector(".nm-app__sidebar");
    const backdrop = document.querySelector("[data-sidebar-backdrop]");
    if (sidebar) sidebar.classList.remove("is-open");
    if (backdrop) {
      backdrop.classList.remove("is-open");
      backdrop.hidden = true;
    }
  }
}
